import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./BlogDetails.css";
import { toast } from "react-toastify";
import { deleteBlog, getBlogById } from "../../../api/blogs";
import { resolveBlogImageSrc } from "../../../utils/blogImages";
import { resolveBlogPdfUrl, blogPdfFileName } from "../../../utils/blogPdf";
import { sanitizeBlogHtml } from "../../../utils/blogHtml";
import { blogStatusLabel, isBlogPublished } from "../../../utils/blogStatus";
import { readingTimeLabelFromBlog } from "../../../utils/readingTime";
import {
    ArrowLeft,
    BookOpen,
    Calendar,
    Clock,
    ExternalLink,
    FileEdit,
    Globe2,
    History,
    Layers,
    Pencil,
    Trash2,
    UserRound,
} from "lucide-react";

const BlogDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const data = await getBlogById(id);
                setBlog(data);
            } catch (err) {
                setError("Failed to load blog. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
    }, [id]);

    const createdLabel = useMemo(() => {
        if (!blog?.createdAt) return "—";
        try {
            return new Date(blog.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    }, [blog?.createdAt]);

    const readingTimeLabel = useMemo(() => {
        if (!blog) return "1 min read";
        return readingTimeLabelFromBlog(blog);
    }, [blog]);

    const pdfSrc = useMemo(() => resolveBlogPdfUrl(blog?.pdfUrl), [blog?.pdfUrl]);

    const updatedLabel = useMemo(() => {
        if (!blog?.updatedAt) return null;
        try {
            const u = new Date(blog.updatedAt);
            if (Number.isNaN(u.getTime())) return null;
            const cTime = blog.createdAt ? new Date(blog.createdAt).getTime() : 0;
            if (u.getTime() <= cTime + 2000) return null;
            return u.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return null;
        }
    }, [blog?.updatedAt, blog?.createdAt]);

    const authorLabel = (blog?.author || "Anonymous").toString();
    const publicSlug = blog?.slug?.trim();
    const isLive = blog ? isBlogPublished(blog) : false;
    const hasPublicLink = Boolean(publicSlug && isLive);

    if (loading) {
        return (
            <section className="blogd" aria-label="Blog details loading">
                <div className="blogd-skel blogd-skel-topbar" />
                <div className="blogd-skel blogd-skel-hero" />
                <div className="blogd-skel blogd-skel-panel" />
            </section>
        );
    }

    if (error) {
        return (
            <section className="blogd" aria-label="Blog details error">
                <div className="blogd-state blogd-state--error" role="alert">
                    <div className="blogd-state-icon" aria-hidden>
                        <BookOpen size={28} strokeWidth={1.75} />
                    </div>
                    <h2 className="blogd-state-title">Could not load this post</h2>
                    <p className="blogd-state-text">{error}</p>
                    <Link className="blogd-btn blogd-btn--secondary" to="/dashboard/blog-list">
                        <ArrowLeft size={17} strokeWidth={2} aria-hidden />
                        Back to list
                    </Link>
                </div>
            </section>
        );
    }

    if (!blog) {
        return (
            <section className="blogd" aria-label="Blog details empty">
                <div className="blogd-state">
                    <div className="blogd-state-icon blogd-state-icon--muted" aria-hidden>
                        <Layers size={28} strokeWidth={1.75} />
                    </div>
                    <h2 className="blogd-state-title">Post not found</h2>
                    <p className="blogd-state-text">
                        This entry may have been removed or the link is invalid.
                    </p>
                    <Link className="blogd-btn blogd-btn--secondary" to="/dashboard/blog-list">
                        <ArrowLeft size={17} strokeWidth={2} aria-hidden />
                        Back to list
                    </Link>
                </div>
            </section>
        );
    }

    const handleDelete = async () => {
        const ok = window.confirm(`Delete "${blog.title}"? This cannot be undone.`);
        if (!ok) return;

        setDeleting(true);
        try {
            await deleteBlog(id);
            toast.success("Blog deleted.");
            navigate("/dashboard/blog-list");
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete blog.";
            toast.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section className="blogd" aria-label="Blog details">
            <div className="blogd-inner">
                <div className="blogd-topbar">
                    <div className="blogd-topbar-accent" aria-hidden />
                    <div className="blogd-topbar-inner">
                        <Link to="/dashboard/blog-list" className="blogd-back">
                            <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                            Back to list
                        </Link>
                        <div className="blogd-actions">
                            {hasPublicLink ? (
                                <Link
                                    to={`/blogs/${encodeURIComponent(publicSlug)}`}
                                    className="blogd-btn blogd-btn--ghost"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLink size={16} strokeWidth={2} aria-hidden />
                                    View live
                                </Link>
                            ) : null}
                            <Link to={`/dashboard/edit-blog/${id}`} className="blogd-btn blogd-btn--primary">
                                <Pencil size={16} strokeWidth={2} aria-hidden />
                                Edit
                            </Link>
                            <button
                                type="button"
                                className="blogd-btn blogd-btn--danger"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                <Trash2 size={16} strokeWidth={2} aria-hidden />
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>

                <header className="blogd-hero">
                    {blog.image ? (
                        <img
                            className="blogd-hero-image"
                            src={resolveBlogImageSrc(blog.image) || blog.image}
                            alt=""
                        />
                    ) : (
                        <div className="blogd-hero-placeholder" aria-hidden />
                    )}

                    <div className="blogd-hero-overlay" />

                    <div className="blogd-hero-content">
                        <p className="blogd-kicker">
                            <BookOpen size={14} strokeWidth={2} className="blogd-kicker-icon" aria-hidden />
                            Preview
                        </p>
                        <h1 className="blogd-title">{blog.title}</h1>

                        <div className="blogd-meta-row">
                            <span className="blogd-chip">
                                <UserRound size={13} strokeWidth={2} aria-hidden />
                                {authorLabel}
                            </span>
                            <span className="blogd-chip">
                                <Calendar size={13} strokeWidth={2} aria-hidden />
                                Created {createdLabel}
                            </span>
                            {updatedLabel ? (
                                <span className="blogd-chip blogd-chip--muted">
                                    <History size={13} strokeWidth={2} aria-hidden />
                                    Updated {updatedLabel}
                                </span>
                            ) : null}
                            <span className="blogd-chip">
                                <Clock size={13} strokeWidth={2} aria-hidden />
                                {readingTimeLabel}
                            </span>
                            <span
                                className={`blogd-chip blogd-chip--status ${
                                    isLive ? "blogd-chip--status-live" : "blogd-chip--status-draft"
                                }`}
                            >
                                {isLive ? (
                                    <Globe2 size={13} strokeWidth={2} aria-hidden />
                                ) : (
                                    <FileEdit size={13} strokeWidth={2} aria-hidden />
                                )}
                                {blogStatusLabel(blog)}
                            </span>
                        </div>
                    </div>
                </header>

                {blog.intro ? (
                    <section className="blogd-intro" aria-label="Blog intro">
                        <p>{blog.intro}</p>
                    </section>
                ) : null}

                {pdfSrc ? (
                    <section className="blogd-pdf blogd-pdf--name-only" aria-label="Attached PDF">
                        <a
                            className="blogd-pdf-name-link"
                            href={pdfSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {blogPdfFileName(blog?.pdfUrl)}
                        </a>
                    </section>
                ) : null}

                <section className="blogd-content-wrap" aria-label="Blog content">
                    <div
                        className="blogd-content"
                        dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(blog.content || "") }}
                    />
                </section>
            </div>
        </section>
    );
};

export default BlogDetails;
